import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { get } from 'lodash';

import Comodo from 'assets/images/comodo.png';
import FooterDisclosure from './FooterDisclosure';
import SecurityModule from './SecurityModule';

const Footer = () => {
  const { pathname } = useLocation();
  const checkinData = useSelector((state) => get(state, 'workflow.data'));

  const showSecurityModule = pathname === '/' || pathname === '/personal-info';

  const renderStepFooter = () => {
    switch (pathname) {
      case '/':
        return (
          <FooterDisclosure
            isExpand
            label="*Disclaimer"
            text="These loan options are only intended to provide you with an estimate on the loan terms that you may receive if you are approved for a loan and are subject to change, this includes without limitation the amount of collateral required which was calculated using real time market prices and are subject to fluctuations. The amount of required collateral will be rounded up to the nearest hundredth."
            expandedNode={(
              <div className="mt-3 text-xs text-gray-500 leading-relaxed space-y-3">
                <p>
                  <span className="remove-if-no-payment">These estimated loan terms reflect a 0.25% promotional interest rate reduction that you may receive if you verify your bank account using only Instant Verification and enroll in Auto-Pay. </span>The 0.25% promotional interest rate reduction is not available for the 'No Monthly Payment' repayment option. While your selected LTV option will be prioritized for review, you will still be reviewed for all other available LTV options, and you will be afforded an opportunity to select from any pre-approved offers prior to finalizing your loan.
                </p>
                <p>
                  You assume the risk of market fluctuation that may affect collateral coverage and repayment obligations.
                </p>
                <p>
                  All loans through LendingUSA are made by its
                  {' '}
                  <a
                    href="https://lendingusa.com/lending-partners/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 underline"
                  >
                    lending partners
                  </a>
                  . Loans are not available in all states.
                </p>
              </div>
            )}
          />
        );
      case '/personal-info':
        return (
          <FooterDisclosure
            noLabel
            text={(
              <>
                All loans through LendingUSA are made by its
                {' '}
                <a
                  href="https://lendingusa.com/lending-partners/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  lending partners
                </a>
                . Loans are not available in all states.
              </>
            )}
          />
        )
      case '/connect-bank':
        return (
          <div className="border-t border-gray-200 pt-6 mb-10">
            <p className="text-xs text-gray-500 leading-relaxed mb-4">
              The account(s) you verify don't have to include alimony, child support, or separate maintenance income unless you want it considered as a basis for repayment.
            </p>
            {get(checkinData, 'repaymentType') !== 'No Payment' ? (
              <FooterDisclosure
                label="Important Interest Rate Information"
                text={(
                  <>
                    *Promotional Rate: By connecting your bank account through our instant verification system, you remain eligible for your 0.25% promotional interest rate reduction as long as you do not also proceed with the manual verification of any account(s). You will also have to enroll in Auto-Pay to be fully eligible for the 0.25% promotional interest rate reduction.
                    {' '}
                    <span className="font-semibold">The 0.25% promotional interest rate reduction is not available for the 'No Monthly Payment' repayment option.</span>
                    {' '}
                    Please note that you may lose your 0.25% promotional interest rate reduction if you unenroll from Auto-Pay during the loan term.
                  </>
                )}
                isExpand
                labelInSingleLine
                linesToPreview={3}
                expandedNode={(
                  <p className="mt-3 text-xs text-gray-500 leading-relaxed">
                    This promotional rate is subject to credit approval and loan terms. The rate adjustment will be reflected in your final loan agreement. You can change your verification method before finalizing your loan.
                  </p>
                )}
                noBorder
              />
            ) : null}
          </div>
        );
      case '/select-offer':
        return (
          <FooterDisclosure
            label="*Origination Fee"
            text="This loan contains an Origination Fee. The Origination Fee is non-refundable and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer by multiplying the Loan Amount by 0.01. For example, if the Loan Amount is $1,000 the corresponding Origination Fee would be $10 ($1,000 x 0.01 = $10)."
            isExpand
            expandedNode={(
              <div className="mt-3 text-xs text-gray-500 leading-relaxed space-y-3">
                <p>
                  The Origination Fee is included in the principal amount of your loan and is deducted from the loan proceeds that are sent to you, for example if your Loan Amount is $1,000, you will only receive $990 in loan proceeds. Please review your Loan Agreement for additional terms and conditions.
                </p>
                <p>
                  **The collateral amount associated with this LTV will be rounded up to the nearest hundredth which may result in a varying LTV.
                </p>
                <p className="apr-autopay-disclaimer hidden">
                  †These estimated loan terms reflect a 0.25% promotional interest rate reduction that you may receive if you enroll in Auto-Pay.
                </p>
              </div>
            )}
          />
        )
      default:
        return null;
    }
  }

  return (
    <div className="flex flex-col items-center mt-auto">
      {showSecurityModule && <SecurityModule className="w-full" />}

      <div className="max-w-xl w-full">
        <div className="footer-disclosures">
          {renderStepFooter()}
        </div>

        <div className="flex w-full items-center justify-between gap-3 sm:gap-4 pb-8">
          <div className="flex items-center flex-shrink-0">
            <img
              src={Comodo}
              alt="Secured by Sectigo"
              width={160}
              height={50}
              className="h-10 w-auto"
            />
          </div>
          <p className="text-xs text-gray-600 whitespace-nowrap text-wrap text-right">
            Copyright © 2026. LendingUSA, LLC. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Footer;
